import axios from "axios";
import type { AxiosResponse } from "axios";

export interface FlowParticipationData {
   createdAt: string;
   flowId: string;
   id: string;
   isEligible: boolean;
   lastSubmittedBlockId: string;
   localeId: string;
   resetAt: string;
   resumeCode: string;
   screenoutReason: string;
   showParticipationResetMessage: boolean;
   reachedEndAt?: string;
}

export interface FlowParticipationResp {
   token?: string;
   data: FlowParticipationData;
   resumeHistory?: string[];
}

interface CurrentAnswer {
   personal_identifier: string;
   claim_code: string;
}

interface ValidateFlowParticipationClaimResp {
   flowId: string;
   success: boolean;
   answers: any;
   attemptsCount: number;
}

export default class FlowParticipationsApi {
   /**
    * Check if the user has access to a study
    * Used in navigation guard middleware to check access to flow routes
    **/
   public static getOrCreateParticipation = async (flowId: string): Promise<FlowParticipationResp> => {
      const response = await axios.get(`/flow-participations/${flowId}?create_if_not_found`);
      return response.data;
   };

   /**
    * when we GET the flow-participation, a separate resume_history is included outside the data block (calculated on the BE)
    **/
   public static getParticipation = async (flowId: string): Promise<FlowParticipationResp> => {
      const response = await axios.get(`/flow-participations/${flowId}`);
      return response.data;
   };

   /**
    * Anonymous public flows only
    **/
   public static startPublicFlow = async (flowId: string): Promise<FlowParticipationResp> => {
      const response = await axios.post(`/p/flow-participations/${flowId}/start-submission`, {});
      return response.data;
   };

   /**
    * Anonymous public flows only - with resume code
    **/
   public static resumePublicFlow = async (
      flowId: string,
      resumeCode: string,
      config = {}
   ): Promise<FlowParticipationResp> => {
      const response = await axios.post(
         `/p/flows/${flowId}/resume-submission`,
         { resume_code: resumeCode },
         { ...config }
      );
      return response.data;
   };

   /**
    * Create a new flow participation with a study invitation code
    */
   public static createFlowParticipationFromInviteCode = async (
      flowId: string,
      studyInvitationCode: string,
      config = {}
   ) => {
      const response = await axios.get(
         `/flow-participations/${flowId}?create_if_not_found&study_invitation_code=${studyInvitationCode}`,
         config
      );
      return response.data;
   };

   public static validateFlowParticipationClaim = async (
      currentAnswer: CurrentAnswer,
      study_id: string,
      config = {}
   ): Promise<ValidateFlowParticipationClaimResp> => {
      const response: AxiosResponse<ValidateFlowParticipationClaimResp> = await axios.post(
         "/claimable-flow-participations/validate",
         {
            ...currentAnswer,
            study_id,
         },
         config
      );

      return response.data;
   };
}
